import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import Bugsnag from "@bugsnag/js";
import { ChakraProvider } from "@chakra-ui/react";
import { Await, useAsyncValue, useLoaderData } from "react-router-dom";
import { generateClientTheme } from "theme";
import type { AgencySettingsContext } from "types/agency";
import { loadFonts, type LoadFontConfig } from "utils/load-fonts";

const agencyAccountContext = createContext<AgencySettingsContext | undefined>(
  undefined,
);

type AgencySettingsProviderProps = {
  children: ReactNode;
};

export type AgencySettingsLoaderData = {
  agencySettings: Promise<AgencySettingsContext>;
};

export function AgencySettingsProvider({
  children,
}: AgencySettingsProviderProps) {
  const loaderData = useLoaderData() as AgencySettingsLoaderData | undefined;

  return (
    <Await resolve={loaderData?.agencySettings}>
      <AgencySettingsProviderInner>{children}</AgencySettingsProviderInner>
    </Await>
  );
}

function AgencySettingsProviderInner({
  children,
}: AgencySettingsProviderProps) {
  const data = useAsyncValue() as AgencySettingsContext | undefined;

  if (!data) {
    const error = new Error(
      "No preferences or branding data found in AgencySettingsProvider. Make sure you're using a React-Router Loader that provides this data.",
    );

    if (import.meta.env.PROD && !__VR_TEST__) {
      Bugsnag.notify(error);
    }

    throw error;
  }

  return (
    <agencyAccountContext.Provider value={data}>
      <ClientThemeProvider>{children}</ClientThemeProvider>
    </agencyAccountContext.Provider>
  );
}

function ClientThemeProvider({ children }: { children: ReactNode }) {
  const { navigationColor, headingFont, bodyFont, accentColor } =
    useAgencyBranding();

  useLoadAgencyFonts();

  const theme = useMemo(() => {
    return generateClientTheme({
      accentColor,
      navigationColor,
      headingFont: headingFont.name,
      bodyFont: bodyFont.name,
    });
  }, [accentColor, navigationColor, headingFont.name, bodyFont.name]);

  return <ChakraProvider theme={theme}>{children}</ChakraProvider>;
}

export function useAgencyBranding() {
  const { branding } = useAgencySettings();

  const value = useMemo(() => {
    return {
      agencyName: branding?.agency_name,
      primaryLogo: branding?.primary_logo?.urls["log.primary"],
      secondaryLogo: branding?.secondary_logo?.urls["log.secondary"],
      accentColor: branding?.accent_color,
      navigationColor: branding?.navigation_color,
      headingFont: {
        name: branding?.heading_font?.name,
        url: branding?.heading_font?.url,
      },
      bodyFont: {
        name: branding?.body_font?.name,
        url: branding?.body_font?.url,
      },
    };
  }, [branding]);

  return value;
}

function useLoadAgencyFonts() {
  const { headingFont, bodyFont } = useAgencyBranding();

  useEffect(() => {
    const fontConfig: LoadFontConfig[] = [];

    let loadedFonts: ReturnType<typeof loadFonts> | undefined;

    if (headingFont.name && headingFont.url) {
      fontConfig.push({
        name: headingFont.name,
        url: headingFont.url,
        selectors: ["h1", "h2", "h3", "h4", "h5", "h6"],
      });
    }

    if (bodyFont.name && bodyFont.url) {
      fontConfig.push({
        name: bodyFont.name,
        url: bodyFont.url,
        selectors: ["p", "span", "div"],
      });
    }

    if (fontConfig.length > 0) {
      loadedFonts = loadFonts(fontConfig);
    }

    return () => {
      loadedFonts?.unloadAll();
    };
  }, [headingFont, bodyFont]);
}

export function useAgencySettings() {
  const context = useContext(agencyAccountContext);

  if (!context) {
    const error = new Error(
      "useAgencySettings must be used within an AgencySettingsProvider",
    );

    if (import.meta.env.PROD && !__VR_TEST__) {
      Bugsnag.notify(error);
    }

    throw error;
  }

  return context;
}
